import { useGlobalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { Button } from "~/components/Button";
import { PasswordInput } from "~/components/form/PasswordInput";
import { TextInput } from "~/components/form/TextInput";
import { GuestFieldGroup } from "~/components/layout/guest/GuestFieldGroup";
import { Page } from "~/components/layout/Page";
import { BodyText, Title } from "~/components/layout/text";
import type { ApiError } from "~/providers/ApiProvider";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useSession } from "~/providers/SessionProvider";
import { empty, filled, isEmail } from "~/util";

export default function ResetPassword() {
    const { token, email }: { token: string; email?: string } = useGlobalSearchParams();
    const { resetPassword } = useSession();
    const { loading, setLoading } = useFeedback();
    const errorBag = useErrorBag<"email" | "password" | "token">();
    const [username, setUsername] = useState(email ?? "");
    const [password, setPassword] = useState("");

    const can_submit = !loading && isEmail(username) && filled(password);

    // biome-ignore lint/correctness/useExhaustiveDependencies( errorBag.forget): error bag
    useEffect(() => {
        errorBag.forget(["email", "password", "token"]);
        setPassword("");
        setUsername(email ?? "");
    }, [email]);

    const attemptReset = () => {
        if (!isEmail(username)) {
            errorBag.add("email", "Email address is not valid");
        }
        if (empty(password)) {
            errorBag.add("password", "Password must not be blank");
        }

        if (!can_submit) {
            return;
        }

        setLoading(true);

        resetPassword({ token, email: username, password })
            .catch((error: ApiError) => {
                if (!error.response?.data?.errors) {
                    errorBag.add("email", error.response?.data?.message ?? "Could not reset your password.");
                }
            })
            .finally(() => setLoading(false));
    };

    return (
        <Page gradient spacer>
            <GuestFieldGroup>
                <Title>Reset password</Title>
                <BodyText>
                    Use this form to change your password. Any devices that are currently logged in will be logged out,
                    including this device.
                </BodyText>
                <TextInput
                    name="email"
                    label="Email address"
                    autoComplete="username"
                    autoCapitalize={"none"}
                    textContentType="emailAddress"
                    value={username}
                    onChangeText={setUsername}
                    disabled={loading}
                />
                <PasswordInput
                    name="password"
                    autoComplete="new-password"
                    autoCapitalize={"none"}
                    value={password}
                    onChangeText={setPassword}
                    disabled={loading}
                />
                <Button
                    title="Reset password"
                    variant="primary"
                    size="lg"
                    onPress={attemptReset}
                    loading={loading}
                    disabled={!can_submit}
                />
            </GuestFieldGroup>
        </Page>
    );
}
